// frontend/src/utils/dateUtils.js - VERSIÓN MEJORADA COMBINADA
import { format, parseISO, isValid, differenceInYears, differenceInDays, 
         startOfDay, endOfDay, addDays, subDays, isBefore, isAfter, 
         isToday, isTomorrow, isYesterday } from 'date-fns';
import { es } from 'date-fns/locale';

// ========== FUNCIONES ORIGINALES MANTENIDAS ==========

/**
 * Formatea una fecha para mostrarla en la interfaz,
 * corrigiendo problemas de zona horaria
 * @param {string|Date} date - Fecha a formatear
 * @param {string} formatStr - Formato a usar
 * @returns {string} Fecha formateada
 */
export const formatDate = (date, formatStr = 'dd/MM/yyyy') => {
  if (!date) return '';
  
  try {
    // Si es string, usar parseISO para convertirlo a Date
    const parsedDate = typeof date === 'string' ? parseISO(date) : date;
    
    if (!isValid(parsedDate)) {
      return 'Fecha inválida';
    }
    
    // Crear una nueva fecha con la misma fecha pero con hora fija a mediodía
    // para evitar problemas de zona horaria
    const dateWithNoon = new Date(
      parsedDate.getFullYear(),
      parsedDate.getMonth(),
      parsedDate.getDate(),
      12, 0, 0
    );
    
    return format(dateWithNoon, formatStr, { locale: es });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Fecha inválida';
  }
};

/**
 * Formatea una hora para mostrarla en la interfaz
 * @param {string} time - Hora en formato HH:mm:ss
 * @returns {string} Hora formateada (formato 12h)
 */
export const formatTime = (time) => {
  if (!time) return '';
  
  try {
    // Si ya viene en formato HH:MM, devolverlo tal como está
    if (typeof time === 'string' && /^\d{2}:\d{2}$/.test(time)) {
      return time;
    }
    
    // Si viene como string en formato HH:MM:SS, extraer solo HH:MM
    if (typeof time === 'string' && /^\d{2}:\d{2}:\d{2}$/.test(time)) {
      return time.substring(0, 5);
    }
    
    // NO usar parseISO para evitar conversiones de zona horaria
    // En su lugar, procesar la cadena directamente
    const timeMatch = time.match(/(\d{1,2}):(\d{2})/);
    if (timeMatch) {
      return `${timeMatch[1].padStart(2, '0')}:${timeMatch[2]}`;
    }
    
    return time;
  } catch (error) {
    console.error('Error formatting time:', error);
    return time;
  }
};

/**
 * Formatea una fecha completa (fecha y hora)
 * @param {string|Date} datetime - Fecha y hora a formatear
 * @returns {string} Fecha y hora formateada
 */
export const formatDateTime = (datetime, formatStr = 'dd/MM/yyyy HH:mm') => {
  if (!datetime) return '';
  
  try {
    // Si es string, usar parseISO para convertirlo a Date
    const parsedDate = typeof datetime === 'string' ? parseISO(datetime) : datetime;
    
    if (!isValid(parsedDate)) {
      return 'Fecha inválida';
    }
    
    // Para fechas con hora, es importante preservar la hora original
    // pero asegurarnos de que estamos interpretando correctamente la fecha
    
    return format(parsedDate, formatStr, { locale: es });
  } catch (error) {
    console.error('Error formatting datetime:', error);
    return 'Fecha inválida';
  }
};

/**
 * Calcula la edad a partir de una fecha de nacimiento
 * @param {string|Date} birthDate - Fecha de nacimiento
 * @returns {number} Edad calculada
 */
export const calculateAge = (birthDate) => {
  if (!birthDate) return 0;
  
  try {
    const today = new Date();
    
    // Si es string, convertirlo a Date
    let birthdateObj;
    if (typeof birthDate === 'string') {
      // Parsear la fecha y fijar la hora a mediodía para evitar problemas de zona horaria
      const parsed = parseISO(birthDate);
      birthdateObj = new Date(
        parsed.getFullYear(),
        parsed.getMonth(),
        parsed.getDate(),
        12, 0, 0
      );
    } else {
      birthdateObj = birthDate;
    }
    
    if (!isValid(birthdateObj)) {
      return 0;
    }
    
    // Usar date-fns para cálculo más preciso
    return differenceInYears(today, birthdateObj);
  } catch (error) {
    console.error('Error calculating age:', error);
    return 0;
  }
};

/**
 * Formatea una fecha ISO para mostrarla en inputs de tipo date (YYYY-MM-DD)
 * @param {string|Date} date - Fecha a formatear
 * @returns {string} Fecha formateada en formato YYYY-MM-DD
 */
export const formatDateForInput = (date) => {
  if (!date) return '';
  
  try {
    // Si es string, usar parseISO para convertirlo a Date
    const parsedDate = typeof date === 'string' ? parseISO(date) : date;
    
    if (!isValid(parsedDate)) {
      return '';
    }
    
    // Crear una nueva fecha con la misma fecha pero con hora fija a mediodía
    const dateWithNoon = new Date(
      parsedDate.getFullYear(),
      parsedDate.getMonth(),
      parsedDate.getDate(),
      12, 0, 0
    );
    
    return format(dateWithNoon, 'yyyy-MM-dd');
  } catch (error) {
    console.error('Error formatting date for input:', error);
    return '';
  }
};

// ========== FUNCIONES NUEVAS AÑADIDAS ==========

/**
 * Formatea una hora para input de tipo time (HH:MM)
 * @param {string|Date} hora - La hora a formatear
 * @returns {string} Hora en formato HH:MM
 */
export const formatTimeForInput = (hora) => {
  if (!hora) return '';
  
  try {
    // Si ya viene en formato HH:MM
    if (typeof hora === 'string' && /^\d{2}:\d{2}$/.test(hora)) {
      return hora;
    }
    
    const date = new Date(hora);
    
    if (!isValid(date)) {
      return '';
    }
    
    return date.toTimeString().substring(0, 5);
  } catch (error) {
    console.error('Error al formatear hora para input:', error);
    return '';
  }
};

/**
 * Obtiene el nombre del día de la semana en español
 * @param {string|Date} fecha - La fecha
 * @returns {string} Nombre del día
 */
export const getDiaSemana = (fecha) => {
  if (!fecha) return '';
  
  try {
    const date = typeof fecha === 'string' ? parseISO(fecha) : fecha;
    
    if (!isValid(date)) {
      return '';
    }
    
    return format(date, 'EEEE', { locale: es });
  } catch (error) {
    console.error('Error al obtener día de la semana:', error);
    return '';
  }
};

/**
 * Obtiene el nombre del mes en español
 * @param {string|Date} fecha - La fecha
 * @returns {string} Nombre del mes
 */
export const getMes = (fecha) => {
  if (!fecha) return '';
  
  try {
    const date = typeof fecha === 'string' ? parseISO(fecha) : fecha;
    
    if (!isValid(date)) {
      return '';
    }
    
    return format(date, 'MMMM', { locale: es });
  } catch (error) {
    console.error('Error al obtener mes:', error);
    return '';
  }
};

/**
 * Verifica si una fecha es hoy
 * @param {string|Date} fecha - La fecha a verificar
 * @returns {boolean} True si es hoy
 */
export const esHoy = (fecha) => {
  if (!fecha) return false;
  
  try {
    const date = typeof fecha === 'string' ? parseISO(fecha) : fecha;
    return isToday(date);
  } catch (error) {
    return false;
  }
};

/**
 * Verifica si una fecha es mañana
 * @param {string|Date} fecha - La fecha a verificar
 * @returns {boolean} True si es mañana
 */
export const esManana = (fecha) => {
  if (!fecha) return false;
  
  try {
    const date = typeof fecha === 'string' ? parseISO(fecha) : fecha;
    return isTomorrow(date);
  } catch (error) {
    return false;
  }
};

/**
 * Verifica si una fecha es ayer
 * @param {string|Date} fecha - La fecha a verificar
 * @returns {boolean} True si es ayer
 */
export const esAyer = (fecha) => {
  if (!fecha) return false;
  
  try {
    const date = typeof fecha === 'string' ? parseISO(fecha) : fecha;
    return isYesterday(date);
  } catch (error) {
    return false;
  }
};

/**
 * Verifica si una fecha es en el pasado
 * @param {string|Date} fecha - La fecha a verificar
 * @returns {boolean} True si es en el pasado
 */
export const esPasado = (fecha) => {
  if (!fecha) return false;
  
  try {
    const date = typeof fecha === 'string' ? parseISO(fecha) : fecha;
    const hoy = startOfDay(new Date());
    
    return isBefore(date, hoy);
  } catch (error) {
    return false;
  }
};

/**
 * Verifica si una fecha es en el futuro
 * @param {string|Date} fecha - La fecha a verificar
 * @returns {boolean} True si es en el futuro
 */
export const esFuturo = (fecha) => {
  if (!fecha) return false;
  
  try {
    const date = typeof fecha === 'string' ? parseISO(fecha) : fecha;
    const hoy = endOfDay(new Date());
    
    return isAfter(date, hoy);
  } catch (error) {
    return false;
  }
};

/**
 * Calcula los días entre dos fechas
 * @param {string|Date} fechaInicio - Fecha de inicio
 * @param {string|Date} fechaFin - Fecha de fin
 * @returns {number} Número de días
 */
export const diasEntre = (fechaInicio, fechaFin) => {
  if (!fechaInicio || !fechaFin) return 0;
  
  try {
    const inicio = typeof fechaInicio === 'string' ? parseISO(fechaInicio) : fechaInicio;
    const fin = typeof fechaFin === 'string' ? parseISO(fechaFin) : fechaFin;
    
    if (!isValid(inicio) || !isValid(fin)) {
      return 0;
    }
    
    return differenceInDays(fin, inicio);
  } catch (error) {
    console.error('Error al calcular días entre fechas:', error);
    return 0;
  }
};

/**
 * Formatea un rango de fechas
 * @param {string|Date} fechaInicio - Fecha de inicio
 * @param {string|Date} fechaFin - Fecha de fin
 * @returns {string} Rango formateado
 */
export const formatDateRange = (fechaInicio, fechaFin) => {
  if (!fechaInicio && !fechaFin) return 'Sin fechas';
  if (!fechaInicio) return `Hasta ${formatDate(fechaFin)}`;
  if (!fechaFin) return `Desde ${formatDate(fechaInicio)}`;
  
  return `${formatDate(fechaInicio)} - ${formatDate(fechaFin)}`;
};

/**
 * Obtiene una fecha relativa (ej: "hace 2 días", "en 3 días")
 * @param {string|Date} fecha - La fecha
 * @returns {string} Descripción relativa
 */
export const getFechaRelativa = (fecha) => {
  if (!fecha) return '';
  
  try {
    const date = typeof fecha === 'string' ? parseISO(fecha) : fecha;
    
    if (!isValid(date)) {
      return 'Fecha inválida';
    }
    
    if (isToday(date)) return 'Hoy';
    if (isTomorrow(date)) return 'Mañana';
    if (isYesterday(date)) return 'Ayer';
    
    const hoy = new Date();
    const diferenciaDias = differenceInDays(date, hoy);
    
    if (diferenciaDias > 1) return `En ${diferenciaDias} días`;
    if (diferenciaDias < -1) return `Hace ${Math.abs(diferenciaDias)} días`;
    
    return formatDate(fecha);
  } catch (error) {
    console.error('Error al obtener fecha relativa:', error);
    return 'Error en fecha';
  }
};

/**
 * Valida si una fecha es válida
 * @param {string|Date} fecha - La fecha a validar
 * @returns {boolean} True si es válida
 */
export const esFechaValida = (fecha) => {
  if (!fecha) return false;
  
  try {
    const date = typeof fecha === 'string' ? parseISO(fecha) : fecha;
    return isValid(date);
  } catch (error) {
    return false;
  }
};

/**
 * Obtiene el primer día del mes
 * @param {string|Date} fecha - Fecha de referencia
 * @returns {Date} Primer día del mes
 */
export const primerDiaDelMes = (fecha = new Date()) => {
  const date = typeof fecha === 'string' ? parseISO(fecha) : fecha;
  return new Date(date.getFullYear(), date.getMonth(), 1);
};

/**
 * Obtiene el último día del mes
 * @param {string|Date} fecha - Fecha de referencia
 * @returns {Date} Último día del mes
 */
export const ultimoDiaDelMes = (fecha = new Date()) => {
  const date = typeof fecha === 'string' ? parseISO(fecha) : fecha;
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
};

/**
 * Convierte minutos a formato HH:MM
 * @param {number} minutos - Cantidad de minutos
 * @returns {string} Tiempo en formato HH:MM
 */
export const minutosAHoras = (minutos) => {
  if (!minutos || minutos < 0) return '00:00';
  
  const horas = Math.floor(minutos / 60);
  const mins = minutos % 60;
  
  return `${horas.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

/**
 * Convierte formato HH:MM a minutos
 * @param {string} tiempo - Tiempo en formato HH:MM
 * @returns {number} Cantidad de minutos
 */
export const horasAMinutos = (tiempo) => {
  if (!tiempo) return 0;
  
  const [horas, minutos] = tiempo.split(':').map(Number);
  return (horas * 60) + minutos;
};

/**
 * Añade días a una fecha
 * @param {string|Date} fecha - Fecha base
 * @param {number} dias - Cantidad de días a añadir
 * @returns {Date} Nueva fecha
 */
export const añadirDias = (fecha, dias) => {
  const date = typeof fecha === 'string' ? parseISO(fecha) : fecha;
  return addDays(date, dias);
};

/**
 * Resta días a una fecha
 * @param {string|Date} fecha - Fecha base
 * @param {number} dias - Cantidad de días a restar
 * @returns {Date} Nueva fecha
 */
export const restarDias = (fecha, dias) => {
  const date = typeof fecha === 'string' ? parseISO(fecha) : fecha;
  return subDays(date, dias);
};

/**
 * Obtiene fechas de la semana actual
 * @returns {Object} Objeto con inicio y fin de semana
 */
export const fechasSemanaActual = () => {
  const hoy = new Date();
  const diaSemana = hoy.getDay();
  const lunes = new Date(hoy);
  lunes.setDate(hoy.getDate() - diaSemana + 1);
  
  const domingo = new Date(lunes);
  domingo.setDate(lunes.getDate() + 6);
  
  return {
    inicio: lunes,
    fin: domingo
  };
};

/**
 * Formatea duración en minutos a texto legible
 * @param {number} minutos - Duración en minutos
 * @returns {string} Duración formateada
 */
export const formatearDuracion = (minutos) => {
  if (!minutos) return '0 min';
  
  if (minutos < 60) {
    return `${minutos} min`;
  }
  
  const horas = Math.floor(minutos / 60);
  const mins = minutos % 60;
  
  if (mins === 0) {
    return `${horas}h`;
  }
  
  return `${horas}h ${mins}m`;
};